(() => {
  if (window.__ultraReferencePerformanceLoaded) return;
  window.__ultraReferencePerformanceLoaded = true;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const SAVE_DATA = Boolean(navigator.connection && navigator.connection.saveData);
  const scriptPromises = window.__ultraReferenceScriptPromises || new Map();
  window.__ultraReferenceScriptPromises = scriptPromises;

  const isVisibleLayout = (video) => video.offsetParent !== null;

  const hydratePoster = (video) => {
    if (!video) return;
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    }
  };

  const hydrateVideo = (video, shouldPlay = true) => {
    if (!video || video.dataset.ultraHydrated === 'true') {
      if (shouldPlay && video && !REDUCED_MOTION.matches) video.play().catch(() => {});
      return;
    }

    const responsiveSource = MOBILE.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
    hydratePoster(video);
    if (responsiveSource) {
      const source = document.createElement('source');
      source.src = responsiveSource;
      source.type = 'video/mp4';
      video.appendChild(source);
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.querySelectorAll('img[data-src]').forEach((image) => {
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    });

    video.dataset.ultraHydrated = 'true';
    video.load();
    if (shouldPlay && !REDUCED_MOTION.matches) video.play().catch(() => {});
  };

  const loadScript = (source) => {
    if (!source) return Promise.resolve();
    if (scriptPromises.has(source)) return scriptPromises.get(source);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = source;
      script.async = false;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    scriptPromises.set(source, promise);
    return promise;
  };

  const hydrateScripts = (root) => {
    if (!root || root.dataset.ultraScriptsHydrated === 'true') return;
    root.dataset.ultraScriptsHydrated = 'true';
    const sources = Array.from(root.querySelectorAll('script[data-ultra-lazy-script]'))
      .map((script) => script.dataset.src)
      .filter(Boolean);
    sources.reduce((chain, source) => chain.then(() => loadScript(source)), Promise.resolve())
      .catch(() => { root.dataset.ultraScriptsHydrated = 'error'; });
  };

  const initialise = (scope = document) => {
    const videos = Array.from(scope.querySelectorAll('[data-ultra-reference-story] video, [data-ultra-motor-video]'));
    if (!videos.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && isVisibleLayout(video)) {
          if (SAVE_DATA || REDUCED_MOTION.matches) hydratePoster(video);
          else hydrateVideo(video, true);
        } else if (!entry.isIntersecting && !video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '500px 0px', threshold: 0.01 });

    videos.forEach((video) => observer.observe(video));

    const lazyRoots = Array.from(scope.querySelectorAll('[data-ultra-lazy-root]'));
    if (lazyRoots.length) {
      const scriptObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrateScripts(entry.target);
          scriptObserver.unobserve(entry.target);
        });
      }, { rootMargin: '900px 0px', threshold: 0.01 });
      lazyRoots.forEach((root) => scriptObserver.observe(root));
    }

    videos.forEach((video) => {
      const root = video.closest('[data-gm3-motors]');
      const button = root && root.querySelector('[data-ultra-video-toggle]');
      if (!button) return;
      const syncButton = () => {
        button.setAttribute('aria-pressed', String(!video.paused));
        button.setAttribute('aria-label', video.paused ? 'Play motor animation' : 'Pause motor animation');
      };
      video.addEventListener('play', syncButton);
      video.addEventListener('pause', syncButton);
      syncButton();
    });

    scope.querySelectorAll('[data-ultra-video-toggle]').forEach((button) => {
      if (button.dataset.ultraVideoBound === 'true') return;
      button.dataset.ultraVideoBound = 'true';
      button.addEventListener('click', () => {
        const root = button.closest('[data-gm3-motors]');
        const video = root && root.querySelector('[data-ultra-motor-video]');
        if (!video) return;
        if (video.dataset.ultraHydrated !== 'true') {
          hydrateVideo(video, false);
        }
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
